// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { CaseStatusForm, CaseStatusValues } from '@Organisms/Case/CaseStatusManage/CaseStatusForm';
import { CaseEventList } from '@Organisms/Case/eventList';
import THDrawer, { THDrawerProps } from '@Molecules/THDrawer/THDrawer';
import { useLocalMessage } from '@Helpers/locale';
import useValidateForm from '@Hooks/useValidateForm';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { CaseImpactStatus, CaseOutput, CaseResolutionStatus, CustomStatusOutput, TaskStatus } from 'thehive-sdk';

import './CaseStatusManage.scss';

interface CaseStatusManageProps extends Omit<THDrawerProps, 'title' | 'children'> {
    onEdit: CaseEventList['onEdit'];
    caseId: string;
    onFinish?: () => void;
    closed?: boolean;
    data?: CaseOutput;
    statusList?: CustomStatusOutput[];
    disabledToggleDrawer?: boolean;
}

const CaseStatusManage = ({
    onEdit,
    data,
    caseId,
    closed = true,
    statusList,
    disabledToggleDrawer = false,
    onFinish,
    ...propsDrawer
}: CaseStatusManageProps): ReactElement => {
    const i = useLocalMessage();

    const { data: taskList = [], refetch: refetchTaskList } = useListQuery({
        queryParams: {
            selector: {
                head: [
                    {
                        name: 'getCase',
                        idOrName: caseId,
                    },
                    { name: 'tasks' },
                ],
                name: 'get-open-tasks',
                initialFilters: [
                    {
                        field: 'status',
                        operator: 'any',
                        values: [TaskStatus.IN_PROGRESS, TaskStatus.WAITING],
                        type: 'string',
                    },
                ],
                sort: ['+flag', '+order', '+startDate', '+title'],
                extraData: ['logCount'],
            },
            paginationOptions: {
                itemsPerPage: 100,
                page: 0,
            },
        },
        controller: client.TaskController,
        manual: true,
    });

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const {
        customFields: caseCustomFields = [],
        impactStatus = CaseImpactStatus.NOT_APPLICABLE,
        summary,
        status,
    } = data || {};

    const onFinishHandler = ({ impactStatus, customFields, ...values }: CaseStatusValues): void => {
        let opt = {};
        if (closed) {
            opt = {
                impactStatus: values.status !== CaseResolutionStatus.TRUE_POSITIVE ? undefined : impactStatus,
                customFields: customFields?.length ? customFields : undefined,
            };
        }

        onEdit(
            {
                ...values,
                ...opt,
            },
            caseId,
        );
    };

    return (
        <THDrawer
            {...propsDrawer}
            title={i(`case.actions.${closed ? 'close' : 'reopen'}.title-drawer`, {
                caseNumber: `#${data?.number || 0}`,
            })}
            className="drawer-form"
            afterVisibleChange={(visible) => {
                if (visible) {
                    refetchTaskList();
                }
                form.resetFields();
            }}
            disableClose={fieldsTouched}
            destroyOnClose
        >
            {(toggleDrawer) => (
                <div className="case-status-manage">
                    <CaseStatusForm
                        isValid={isValid && fieldsTouched}
                        caseId={caseId}
                        form={form}
                        onChange={() => {
                            setFieldsTouched(true);
                            onFormValuesChange();
                        }}
                        taskList={taskList}
                        statusList={statusList || []}
                        onFinish={(values) => {
                            onFinishHandler(values);
                            if (!disabledToggleDrawer) {
                                toggleDrawer();
                            }
                            onFinish?.();
                            form.resetFields();
                        }}
                        onCancel={() => {
                            setFieldsTouched(false);
                            toggleDrawer();
                            form.resetFields();
                        }}
                        closed={closed}
                        initialValues={
                            closed
                                ? {
                                      status,
                                      impactStatus:
                                          impactStatus !== CaseImpactStatus.NOT_APPLICABLE ? impactStatus : undefined,
                                      summary,
                                      customFields: caseCustomFields,
                                  }
                                : { status }
                        }
                    />
                </div>
            )}
        </THDrawer>
    );
};

export default CaseStatusManage;
